import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { UserEntity } from '@data/index';
import { useOrganizations } from '@data/hooks/organizations';
import { useStore } from '@data/storages';
import { useOrganizationId } from '@app/utils/hooks';
import { Roles } from '@app/utils/types';
import { cardClass, pageHeaderRowClass, pageTitleClass } from '@app/constants/styles';
import { PHILL_BOT_HASH, clientsReportingPath } from '@app/constants/url';
import { useBackNavigation } from '@app/components/core/useBackNavigation';
import { hasOnboarded } from '@app/components/core/philBot/onboardingState';
import { clearPhilBotReturn, hasPhilBotReturn } from '@app/components/core/philBot/returnTrip';
import AccountSection from './AccountSection';
import ChangePasswordSection from './ChangePasswordSection';
import GeneralSection from './GeneralSection';
import PhilBotSection from './PhilBotSection';

const SectionHeading = ({ title }: { title: string }): JSX.Element => (
  <h2 className="mb-5 text-lg font-bold text-foreground">{title}</h2>
);

// A page of its own for both shells, reached from the profile menu. Every section is on it at once.
const SettingsPage = ({ user }: { user: UserEntity }): JSX.Element => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId();
  const { organization } = useStore();
  const { data: organizations } = useOrganizations(user.id);
  const role = organization?.role ?? organizations?.items.find(org => org.id === organizationId)?.role;
  // Unknown counts as a client, so a client never sees employee sections flash in while roles load.
  const isEmployee = role !== undefined && role !== Roles.client;
  // A client has no sidebar, so like their other pages this one leads back to their products.
  const { label: backLabel, goBack } = useBackNavigation(
    clientsReportingPath(organizationId ?? ''),
    t('actions.backToProducts'),
  );

  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  // Connecting an app sends the browser back here; the welcome wizard owns that trip until it is done.
  const [returnedFromConnect] = useState(() => hasOnboarded(user.id) && hasPhilBotReturn());
  const philBotRef = useRef<HTMLElement>(null);
  const { hash } = useLocation();

  useEffect(() => {
    if (!returnedFromConnect || !isEmployee) return;
    clearPhilBotReturn();
    philBotRef.current?.scrollIntoView({ block: 'start' });
  }, [returnedFromConnect, isEmployee]);

  // Sent here from the drafting settings to connect a source.
  useEffect(() => {
    if (hash === PHILL_BOT_HASH && isEmployee) philBotRef.current?.scrollIntoView({ block: 'start' });
  }, [hash, isEmployee]);

  return (
    <>
      <Helmet>
        <title>{t('settings.title')}</title>
      </Helmet>
      <div className="flex w-full min-w-0 flex-col gap-5">
        <div>
          {role === Roles.client && (
            <button
              type="button"
              onClick={goBack}
              className="mb-3.5 flex w-fit items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              <ChevronLeft className="size-4" />
              {backLabel}
            </button>
          )}
          <div className={`${pageHeaderRowClass} flex items-center`}>
            <h1 className={pageTitleClass}>{t('settings.title')}</h1>
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <section className={cardClass}>
            <SectionHeading title={changingPassword ? t('settings.password.change') : t('settings.account.title')} />
            {changingPassword ? (
              <ChangePasswordSection
                onDone={() => {
                  setPasswordUpdated(true);
                  setChangingPassword(false);
                }}
                onCancel={() => setChangingPassword(false)}
              />
            ) : (
              <AccountSection
                user={user}
                updated={passwordUpdated}
                onChangePassword={() => {
                  setPasswordUpdated(false);
                  setChangingPassword(true);
                }}
              />
            )}
          </section>
          {isEmployee && (
            <section className={cardClass}>
              <SectionHeading title={t('settings.general.title')} />
              <GeneralSection user={user} isEmployee />
            </section>
          )}
          {/* Phill bot is an employee's own reporting assistant; a client has nothing to connect. */}
          {isEmployee && (
            <section ref={philBotRef} className={`${cardClass} scroll-mt-6`}>
              <SectionHeading title={t('philBot.title')} />
              <PhilBotSection user={user} />
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export default SettingsPage;
